import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DataProvider } from './lib/provider';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LanguageProvider } from './contexts/LanguageContext';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { OnboardingPage } from './pages/Onboarding';
import { LandingPage } from './pages/LandingPage';
import { ParentLayout } from './layouts/ParentLayout';
import { KidLayout } from './layouts/KidLayout';
import { ParentDashboard } from './pages/ParentDashboard';
import { ParentQuests } from './pages/ParentQuests';
import { ParentRewards } from './pages/ParentRewards';
import { ParentFamily } from './pages/ParentFamily';
import { ParentKidDetails } from './pages/ParentKidDetails';
import { ParentRewardHistory } from './pages/ParentRewardHistory';
import { KidHome } from './pages/KidHome';
import { KidRewards } from './pages/KidRewards';
import { KidRewardHistory } from './pages/KidRewardHistory';
import { Footer } from './components/Footer';

const ProtectedRoute = ({ children, role }: { children: React.ReactNode, role?: 'parent' | 'kid' }) => {
  const { session, isLoading } = useAuth();
  if (isLoading) return <div className="p-8">Loading...</div>;
  if (!session.isAuthenticated) return <Navigate to="/" replace />;

  // If authenticated but no profile/household
  if (!session.user) return <Navigate to="/onboarding" replace />;

  if (role && session.user.role !== role) {
    return <Navigate to={session.user.role === 'parent' ? '/parent' : '/kid'} replace />;
  }
  return <>{children}</>;
};

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DataProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DataProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const { session } = useAuth();
  
  return (
    <LanguageProvider userLanguage={session.user?.language}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />

          <Route path="/parent" element={<ProtectedRoute role="parent"><ParentLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/parent/dashboard" replace />} />
            <Route path="dashboard" element={<ParentDashboard />} />
            <Route path="quests" element={<ParentQuests />} />
            <Route path="rewards" element={<ParentRewards />} />
            <Route path="rewards/history" element={<ParentRewardHistory />} />
            <Route path="family" element={<ParentFamily />} />
            <Route path="family/:kidId" element={<ParentKidDetails />} />
          </Route>

          <Route path="/kid" element={<ProtectedRoute role="kid"><KidLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/kid/home" replace />} />
            <Route path="home" element={<KidHome />} />
            <Route path="rewards" element={<KidRewards />} />
            <Route path="rewards/history" element={<KidRewardHistory />} />
          </Route>
        </Routes>
        <Footer />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
